var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { motionPresets } from './motion/presets';
import { classNames as cn } from './utils';
const NESTED_OVERLAY_TRIGGER_SELECTOR = [
    '[data-slot="dropdown-menu-trigger"][data-state="open"]',
    '[data-slot="popover-trigger"][data-state="open"]',
    '[data-slot="select-trigger"][data-state="open"]',
].join(',');
const NESTED_OVERLAY_CONTENT_SELECTOR = [
    '[data-slot="dropdown-menu-content"]',
    '[data-slot="popover-content"]',
    '[data-slot="select-content"]',
].join(',');
function isDesktopViewport() {
    return (typeof window === 'undefined' ||
        typeof window.matchMedia !== 'function' ||
        window.matchMedia('(min-width: 64rem)').matches);
}
const MOBILE_BOTTOM_SHEET_CLASSES = [
    'max-lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:flex',
    'max-lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:flex-col',
    'max-lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:gap-3',
    'max-lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:rounded-3xl',
    'max-lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:border-brand-200',
    'dark:max-lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:border-base-200',
    'max-lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:pt-3',
    'max-lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:shadow-[0_4px_6px_-1px_rgba(20,28,37,0.08)]',
].join(' ');
const DrawerContentContext = React.createContext(false);
function useIsInsideDrawerContent() {
    return React.useContext(DrawerContentContext);
}
/**
 * Dragging outside the handle is off by default. `DrawerContent` renders a
 * real handle for bottom drawers; side drawers remain non-draggable. Pass
 * `handleOnly={false}` to opt a drawer into swipe-to-close from its content.
 */
function Drawer(_a) {
    var { handleOnly = true } = _a, props = __rest(_a, ["handleOnly"]);
    const Root = useIsInsideDrawerContent()
        ? DrawerPrimitive.NestedRoot
        : DrawerPrimitive.Root;
    return _jsx(Root, Object.assign({ "data-slot": "drawer", handleOnly: handleOnly }, props));
}
function DrawerTrigger(_a) {
    var props = __rest(_a, []);
    return _jsx(DrawerPrimitive.Trigger, Object.assign({ "data-slot": "drawer-trigger" }, props));
}
function DrawerPortal(_a) {
    var props = __rest(_a, []);
    return _jsx(DrawerPrimitive.Portal, Object.assign({ "data-slot": "drawer-portal" }, props));
}
function DrawerClose(_a) {
    var props = __rest(_a, []);
    return _jsx(DrawerPrimitive.Close, Object.assign({ "data-slot": "drawer-close" }, props));
}
function DrawerHandle(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(DrawerPrimitive.Handle, Object.assign({ "data-slot": "drawer-handle", className: cn('bg-base-300! mx-auto hidden! h-0.5! w-[60px]! shrink-0 rounded-[1px]! opacity-100! group-data-[vaul-drawer-direction=bottom]/drawer-content:block! lg:group-data-[vaul-drawer-direction=bottom]/drawer-content:hidden!', className) }, props)));
}
function DrawerOverlay(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(DrawerPrimitive.Overlay, Object.assign({ "data-slot": "drawer-overlay", className: cn(motionPresets.fade, 'fixed inset-0 z-50 bg-black/50', className) }, props)));
}
function DrawerContent(_a) {
    var { className, innerClassName, children, variant = 'rounded', onPointerDownOutside } = _a, props = __rest(_a, ["className", "innerClassName", "children", "variant", "onPointerDownOutside"]);
    return (_jsxs(DrawerPortal, { "data-slot": "drawer-portal", children: [_jsx(DrawerOverlay, {}), _jsx(DrawerPrimitive.Content, Object.assign({ "data-slot": "drawer-content", onPointerDownOutside: (event) => {
                    onPointerDownOutside === null || onPointerDownOutside === void 0 ? void 0 : onPointerDownOutside(event);
                    if (event.defaultPrevented || isDesktopViewport())
                        return;
                    const drawerContent = event.currentTarget;
                    const target = event.detail.originalEvent.target;
                    if ((drawerContent === null || drawerContent === void 0 ? void 0 : drawerContent.querySelector(NESTED_OVERLAY_TRIGGER_SELECTOR)) ||
                        (target === null || target === void 0 ? void 0 : target.closest(NESTED_OVERLAY_CONTENT_SELECTOR))) {
                        event.preventDefault();
                    }
                }, className: cn('group/drawer-content fixed z-50 flex h-auto flex-col', variant === 'rounded' && [
                    'p-2',
                    'data-[vaul-drawer-direction=right]:pt-[max(0.5rem,env(safe-area-inset-top))] data-[vaul-drawer-direction=right]:pr-[max(0.5rem,env(safe-area-inset-right))] data-[vaul-drawer-direction=right]:pb-[max(0.5rem,env(safe-area-inset-bottom))]',
                    'data-[vaul-drawer-direction=left]:pt-[max(0.5rem,env(safe-area-inset-top))] data-[vaul-drawer-direction=left]:pb-[max(0.5rem,env(safe-area-inset-bottom))] data-[vaul-drawer-direction=left]:pl-[max(0.5rem,env(safe-area-inset-left))]',
                ], variant === 'flat' && 'bg-card p-4', 'data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80dvh]', 'data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80dvh]', 'data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:w-3/4 data-[vaul-drawer-direction=right]:sm:max-w-126.5', 'data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-full data-[vaul-drawer-direction=left]:sm:w-3/4 data-[vaul-drawer-direction=left]:sm:max-w-sm', className) }, props, { children: _jsx(DrawerContentContext.Provider, { value: true, children: variant === 'rounded' ? (_jsxs("div", { className: cn('border-base-200 bg-card h-full w-full rounded-2xl border px-4 py-1', MOBILE_BOTTOM_SHEET_CLASSES, innerClassName), children: [_jsx(DrawerHandle, {}), children] })) : (_jsxs(_Fragment, { children: [_jsx(DrawerHandle, {}), children] })) }) }))] }));
}
function DrawerHeader(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx("div", Object.assign({ "data-slot": "drawer-header", className: cn('font-inter flex flex-col gap-1 py-2.5 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left', className) }, props)));
}
function DrawerFooter(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx("div", Object.assign({ "data-slot": "drawer-footer", className: cn('mt-auto flex flex-col gap-2 p-4', className) }, props)));
}
function DrawerTitle(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(DrawerPrimitive.Title, Object.assign({ "data-slot": "drawer-title", className: cn('text-base-900 font-semibold', className) }, props)));
}
function DrawerDescription(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(DrawerPrimitive.Description, Object.assign({ "data-slot": "drawer-description", className: cn('text-base-600 text-sm', className) }, props)));
}
export { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerPortal, DrawerTitle, DrawerTrigger, useIsInsideDrawerContent, };
